// src/components/RootRedirect.tsx
import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import WebApp from '@twa-dev/sdk';
import { apiFetch } from '../lib/api-client.js';
import { useAuth } from '../store/auth.js';
import { STEP_TO_ROUTE, ONBOARDING_STEPS } from '../lib/onboarding-steps.js';
import { routeForToken, parseHead, stashPendingDeepLink, isCourseStart, takeCourseStart } from '../lib/tokenRoutes.js';

// Spec 016 T012: at server step=4 the client has three legitimate routes —
// InfoReveal (default landing), PickPersona (after CTA), and the selfie sub-flow.
// STEP_TO_ROUTE[4] points at info-reveal so a fresh refresh lands there; the
// other routes are accepted via this allow-list to avoid a redirect bounce.
const ALLOWED_FROM_STEP_4 = new Set([
  '/onboarding/info-reveal',
  '/onboarding/pick',
  '/onboarding/selfie',
  '/onboarding/capture',
]);

export function RootRedirect(): null {
  const jwt = useAuth(s => s.jwt);
  const step = useAuth(s => s.onboardingStep);
  const setStep = useAuth(s => s.setOnboardingStep);
  const nav = useNavigate();
  const loc = useLocation();

  useEffect(() => {
    const startParam: string =
      (WebApp?.initDataUnsafe as { start_param?: string } | undefined)?.start_param ?? '';

    // 1) Known landing feature deep-link — takes precedence over the legacy
    //    compat_<userId> invite so e.g. `compat_check` is NOT misread as a userId.
    //    A course link is handled below, only on its first visit to `/` in this session.
    const course = isCourseStart(startParam);
    const featureRoute = course ? null : routeForToken(startParam);

    // 2) Legacy compat invite (compat_<userId>) — only when NOT a known token.
    if (!featureRoute && startParam.startsWith('compat_')) {
      const targetUserId = startParam.slice('compat_'.length);
      nav(`/flow/compat?withUserId=${targetUserId}`, { replace: true });
      return;
    }

    if (!jwt) return;

    // The free course opens straight away at any onboarding step (no age/birth screens first). Leaving it
    // goes to `/`, where the link is not followed again and the normal onboarding starts.
    if (course && takeCourseStart()) {
      if (loc.pathname !== '/course') nav('/course', { replace: true });
      return;
    }

    let cancelled = false;
    void (async () => {
      const r = await apiFetch('/api/v1/me', jwt);
      const j = await r.json() as { onboardingStep: number };
      if (cancelled) return;
      setStep(j.onboardingStep);

      // Landing feature deep-link: route straight there if onboarded, else stash
      // and resume after onboarding completes (Home reads pendingDeepLink on mount).
      if (featureRoute) {
        // natal_reading deep-link auto-opens the reading on NatalChart
        const head = parseHead(startParam);
        const resolvedRoute = head === 'natal_reading'
          ? `${featureRoute}?autoLoad=1`
          : featureRoute;
        if (j.onboardingStep === ONBOARDING_STEPS.DONE) {
          if (loc.pathname !== featureRoute) nav(resolvedRoute, { replace: true });
        } else {
          stashPendingDeepLink(resolvedRoute);
          const target = STEP_TO_ROUTE[j.onboardingStep] ?? '/home';
          if (loc.pathname !== target) nav(target, { replace: true });
        }
        return;
      }

      // Default: resume the correct onboarding/home screen by step.
      if (j.onboardingStep === 4 && ALLOWED_FROM_STEP_4.has(loc.pathname)) return;
      const target = STEP_TO_ROUTE[j.onboardingStep] ?? '/home';
      if (loc.pathname !== target) nav(target, { replace: true });
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jwt]);

  void step; // suppress unused-variable lint; step is read by other callers via the store
  return null;
}
